// src/features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/components/QuantXRayTable.tsx
//
// "Quant X-Ray" holdings table — decomposes the final target weight into its
// Tier-1 (intra-sleeve micro) and Tier-2 (cross-asset macro) drivers and
// prices each position against the user's capital. Presentation only; sorting
// happens server-side (the builder returns rows by descending target_weight).

import { Link } from 'react-router-dom';

import { baseAsset } from '../../../crypto-ranking/api';
import type { GlobalPortfolioRow } from '../api';
import { sleeveColor, sleeveLabel } from './sleeveColors';

// Maps each backend sleeve id onto its dedicated asset-ranking dashboard page,
// so a holding's Asset Class badge deep-links to the leaderboard it came from.
const SLEEVE_RANKING_ROUTES: Record<string, string> = {
    equities: '/dashboard/stock-ranking',
    etf: '/dashboard/etf-ranking',
    commodities: '/dashboard/commodities-ranking',
    reits: '/dashboard/reits-ranking',
    cryptocurrencies: '/dashboard/crypto-ranking',
    fx: '/dashboard/fx-ranking',
};

// Maps sleeve ids that have a single-symbol deep-dive page onto that page,
// so the Symbol cell links straight to the instrument's detail view
// (equities -> stock deep-dive, ETFs -> ETF deep-dive, REITs -> REIT deep-dive,
// cryptocurrencies -> crypto deep-dive, commodities -> commodity deep-dive).
const SYMBOL_DEEPDIVE_ROUTES: Record<string, string> = {
    equities: '/dashboard/stock-info-page',
    etf: '/dashboard/etf-info-page',
    reits: '/dashboard/reit-info-page',
    cryptocurrencies: '/dashboard/crypto-info-page',
    commodities: '/dashboard/commodity-info-page',
    fx: '/dashboard/fx-info-page',
};

// The crypto sleeve keys coins by their FMP USD pair (e.g. "BTCUSD"), but the
// crypto-info deep-dive page + its static description JSON use the bare base
// ticker (e.g. "BTC"). All other sleeves link with their symbol verbatim.
const deepDiveSymbol = (row: GlobalPortfolioRow): string =>
    row.asset_class === 'cryptocurrencies'
        ? baseAsset(row.symbol)
        : row.symbol;

interface QuantXRayTableProps {
    rows: GlobalPortfolioRow[];
    /** Total capital in USD, used to price each weight into dollars. */
    investment: number;
}

const pct = (v: number): string => `${(v * 100).toFixed(2)}%`;

const usd = (v: number): string =>
    `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`;

export const QuantXRayTable = ({ rows, investment }: QuantXRayTableProps) => {
    if (!rows.length) {
        return <p className="muted text-sm">No holdings to display.</p>;
    }

    return (
        <div className="overflow-x-auto max-h-[520px] rounded-xl border border-slate-200 dark:border-slate-700">
            <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 text-[10px] uppercase tracking-wider font-bold border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                    <tr>
                        <th className="p-3.5">Symbol</th>
                        <th className="p-3.5">Asset Class</th>
                        <th
                            className="p-3.5 text-right"
                            title="How much of the asset class budget this symbol consumes"
                        >
                            Weight Within Asset Class
                        </th>
                        <th
                            className="p-3.5 text-right"
                            title="How much of the total portfolio this asset class consumes"
                        >
                            Asset Class Weight
                        </th>
                        <th className="p-3.5 text-right text-indigo-600 dark:text-indigo-400">
                            Total Portfolio Weight
                        </th>
                        <th className="p-3.5 text-right text-indigo-600 dark:text-indigo-400">
                            Target Value
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {rows.map((row) => (
                        <tr
                            key={`${row.asset_class}:${row.symbol}`}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                        >
                            <td className="p-3.5 font-bold text-slate-900 dark:text-white">
                                {SYMBOL_DEEPDIVE_ROUTES[row.asset_class] ? (
                                    <Link
                                        to={`${SYMBOL_DEEPDIVE_ROUTES[row.asset_class]}?symbol=${encodeURIComponent(deepDiveSymbol(row))}`}
                                        title={`View ${row.symbol} deep-dive`}
                                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                                    >
                                        {row.symbol}
                                    </Link>
                                ) : (
                                    row.symbol
                                )}
                            </td>
                            <td className="p-3.5">
                                {SLEEVE_RANKING_ROUTES[row.asset_class] ? (
                                    <Link
                                        to={
                                            SLEEVE_RANKING_ROUTES[
                                                row.asset_class
                                            ]
                                        }
                                        title={`View ${sleeveLabel(row.asset_class)} ranking`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-opacity hover:opacity-80 hover:underline"
                                        style={{
                                            backgroundColor: `${sleeveColor(row.asset_class)}1a`,
                                            color: sleeveColor(row.asset_class),
                                            borderColor: `${sleeveColor(row.asset_class)}33`,
                                        }}
                                    >
                                        {sleeveLabel(row.asset_class)}
                                    </Link>
                                ) : (
                                    <span
                                        className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border"
                                        style={{
                                            backgroundColor: `${sleeveColor(row.asset_class)}1a`,
                                            color: sleeveColor(row.asset_class),
                                            borderColor: `${sleeveColor(row.asset_class)}33`,
                                        }}
                                    >
                                        {sleeveLabel(row.asset_class)}
                                    </span>
                                )}
                            </td>
                            <td className="p-3.5 text-right font-medium text-slate-500 dark:text-slate-400 tabular-nums">
                                {pct(row.tier1_weight)}
                            </td>
                            <td className="p-3.5 text-right font-medium text-slate-500 dark:text-slate-400 tabular-nums">
                                {pct(row.tier2_weight)}
                            </td>
                            <td className="p-3.5 text-right font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">
                                {pct(row.target_weight)}
                            </td>
                            <td className="p-3.5 text-right font-bold text-slate-800 dark:text-slate-100 tabular-nums">
                                {usd(row.target_weight * investment)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default QuantXRayTable;
